import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState } from "react";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@shared/components/ui/form";
import { useAuth } from "@features/auth/contexts/AuthContext";
import { ReloadIcon, EyeOpenIcon, EyeClosedIcon } from "@radix-ui/react-icons";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function LoginForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { isLoginPending, login } = useAuth();

  // local state to toggle password visibility
  const [showPassword, setShowPassword] = useState(false);

  // Define the schema for form validation with translated messages
  const formSchema = z.object({
    identifier: z.string(),
    password: z.string().min(8, t("validation.passwordMinLength", { min: 8 })),
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      identifier: "",
      password: "",
    },
  });

  function onSubmit(values) {
    const toastId = toast.loading(t("auth.loggingIn"));
    login(values, {
      onSuccess: (data) => {
        toast.success(t("auth.loginSuccess", { brandName: data.brandName }), {
          id: toastId,
        });
        navigate("/", { replace: true });
      },
      onError: (error) => {
        toast.error(`${error.response?.data?.message ?? error.message}`, {
          id: toastId,
        });
      },
    });
  }

  return (
    <>
      <div className="text-center py-10">
        <h1 className="text-3xl font-bold"> {t("auth.loginToYourAccount")}</h1>
      </div>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-4 flex flex-col justify-center"
        >
          <FormField
            control={form.control}
            name="identifier"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("user.identifier")}</FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder={t("auth.enter", {
                      entity: t("user.identifier"),
                    })}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("user.password")}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder={t("auth.enter", {
                        entity: t("user.password"),
                      })}
                      {...field}
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowPassword((s) => !s)}
                      aria-label={
                        showPassword
                          ? t("auth.hidePassword")
                          : t("auth.showPassword")
                      }
                      className="absolute rtl:left-2 ltr:right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    >
                      {showPassword ? (
                        <EyeClosedIcon className="w-5 h-5" />
                      ) : (
                        <EyeOpenIcon className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="text-right">
            <Link
              to="/forgot-password"
              className="text-sm text-primary underline"
            >
              {t("auth.forgotPassword")}
            </Link>
          </div>

          <Button type="submit" disabled={isLoginPending}>
            {isLoginPending ? (
              <>
                <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                {t("main.loading")}
              </>
            ) : (
              t("auth.login")
            )}
          </Button>
        </form>
      </Form>
      <div className="mt-4 text-center text-sm">
        {t("auth.dontHaveAccount")}{" "}
        <Link to="/register" className="underline">
          {t("auth.register")}
        </Link>
      </div>
    </>
  );
}
